import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { notesApi } from '../api';
import { useAuth } from '../context/AuthContext';
import NoteCard from '../components/notes/NoteCard';
import NoteModal from '../components/notes/NoteModal';
import NoteDetailModal from '../components/notes/NoteDetailModal';
import Pagination from '../components/common/Pagination';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { Icon } from '../components/common/Icons';
import { toast } from 'react-toastify';

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

const PAGE_SIZE = 12;

export default function DashboardPage() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Data states
  const [notes, setNotes] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filter & Layout states
  const [filterTab, setFilterTab] = useState('all'); // 'all' | 'starred'
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [sortBy, setSortBy] = useState('recent'); // 'recent' | 'hits' | 'title' | 'title_desc'
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('view_mode') || 'grid');

  // Modals
  const [showCreate, setShowCreate] = useState(false);
  const [editNote, setEditNote] = useState(null);
  const [viewNote, setViewNote] = useState(null);
  const [deleteNote, setDeleteNote] = useState(null);

  const toggleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('view_mode', mode);
  };

  // Open create modal if ?new=1 is in URL
  useEffect(() => {
    if (new URLSearchParams(location.search).get('new') === '1') {
      setShowCreate(true);
      navigate('/', { replace: true });
    }
  }, [location.search, navigate]);

  // Fetch paginated notes from backend
  const loadNotes = useCallback(async (targetPage = page, sortParam = sortBy, categoryParam = selectedCategoryId, tabParam = filterTab) => {
    setLoading(true);
    try {
      const { data } = await notesApi.list({
        categoryId: categoryParam != null ? categoryParam : undefined,
        folderId: categoryParam != null ? categoryParam : undefined,
        starred: tabParam === 'starred',
        favorite: tabParam === 'starred',
        sort: sortParam,
        page: targetPage,
        size: PAGE_SIZE,
      });

      if (data && data.content) {
        setNotes(data.content);
        setTotalElements(data.totalElements || 0);
        setTotalPages(data.totalPages || 0);
        setPage(data.page || targetPage);
      } else if (Array.isArray(data)) {
        setNotes(data);
        setTotalElements(data.length);
        setTotalPages(Math.ceil(data.length / PAGE_SIZE));
      }
    } catch {
      setNotes([]);
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, selectedCategoryId, filterTab]);

  useEffect(() => {
    loadNotes(page, sortBy, selectedCategoryId, filterTab);
  }, [loadNotes, page, sortBy, selectedCategoryId, filterTab]);

  // Handle global note-saved events from modals
  useEffect(() => {
    const handleSaved = () => {
      loadNotes(0, sortBy, selectedCategoryId, filterTab);
    };
    window.addEventListener('memora:note-saved', handleSaved);
    window.addEventListener('memora:doc-created', handleSaved);
    return () => {
      window.removeEventListener('memora:note-saved', handleSaved);
      window.removeEventListener('memora:doc-created', handleSaved);
    };
  }, [sortBy, selectedCategoryId, filterTab, loadNotes]);

  // Toggle star
  const handleToggleStar = async (note) => {
    try {
      const { data: updated } = await notesApi.toggleStar(note.id);
      setNotes(prev => {
        const isStarred = !!(updated.starred ?? updated.favorite);
        if (filterTab === 'starred' && !isStarred) {
          return prev.filter(n => n.id !== note.id);
        }
        return prev.map(n => n.id === note.id ? updated : n);
      });
    } catch {
      toast.error('Failed to update star');
    }
  };

  // Soft delete note -> moves to Trash (7-day retention)
  const handleDeleteNote = async () => {
    if (!deleteNote) return;
    try {
      await notesApi.delete(deleteNote.id);
      toast.success(`"${deleteNote.title}" moved to Trash (7-day retention)`);
      setDeleteNote(null);
      // Refresh current page
      loadNotes(page, sortBy, selectedCategoryId);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete note');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage < 0 || newPage >= totalPages) return;
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const firstName = user?.firstName || user?.username || 'there';

  return (
    <div className="dashboard-page notes-view-page">
      {/* Top Banner */}
      <div className="notes-header-hero">
        <div className="notes-hero-text">
          <h1 className="notes-page-title" style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            <Icon name="home" size={24} />
            <span>{greeting()}, {firstName}</span>
          </h1>
          <p className="notes-page-subtitle">Capture thoughts, shape ideas, and keep your notes effortlessly organized.</p>
        </div>
      </div>

      {/* Control Strip / Toolbar */}
      <div className="library-toolbar">
        {/* All Notes / Starred Slider Toggle */}
        <div className="toolbar-title-wrap">
          <div className="search-slider-toggle home-filter-slider" style={{ width: '250px' }}>
            <div className={`search-slider-pill ${filterTab === 'all' ? 'notes' : 'starred'}`} />
            <button
              type="button"
              className={`search-slider-btn ${filterTab === 'all' ? 'active' : ''}`}
              onClick={() => {
                setFilterTab('all');
                setPage(0);
              }}
            >
              <Icon name="note" size={14} />
              <span>All Notes</span>
            </button>
            <button
              type="button"
              className={`search-slider-btn ${filterTab === 'starred' ? 'active' : ''}`}
              onClick={() => {
                setFilterTab('starred');
                setPage(0);
              }}
            >
              <Icon name="star" size={14} filled={filterTab === 'starred'} />
              <span>Starred</span>
            </button>
          </div>
        </div>

        <div className="toolbar-actions">
          {/* Sort Selector with Sort Icon */}
          <div className="sort-dropdown-wrap">
            <Icon name="sort" size={14} className="sort-icon" />
            <select
              className="sort-select"
              value={sortBy}
              onChange={e => {
                setSortBy(e.target.value);
                setPage(0);
              }}
              aria-label="Sort notes"
            >
              <option value="recent">Recently Active</option>
              <option value="hits">Most Viewed</option>
              <option value="title">Alphabetically (A-Z)</option>
              <option value="title_desc">Alphabetically (Z-A)</option>
            </select>
          </div>

          {/* View Mode Toggle */}
          <div className="view-toggle-group">
            <button
              type="button"
              className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => toggleViewMode('grid')}
              title="Grid View"
              aria-label="Grid view"
            >
              <Icon name="grid" size={16} />
            </button>
            <button
              type="button"
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => toggleViewMode('list')}
              title="List View"
              aria-label="List view"
            >
              <Icon name="list" size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Notes List / Grid */}
      {loading ? (
        <div className="loading-state-wrap">
          <div className="spinner" />
          <p className="loading-text">Loading notes...</p>
        </div>
      ) : notes.length === 0 ? (
        <div className="empty-state-card">
          <div className="empty-state-icon-wrap">
            <Icon name={filterTab === 'starred' ? 'star' : 'note'} size={36} filled={filterTab === 'starred'} />
          </div>
          <h2 className="empty-state-title">
            {filterTab === 'starred'
              ? 'No starred notes yet'
              : selectedCategoryId
              ? 'No notes in this category'
              : 'No notes here yet'}
          </h2>
          <p className="empty-state-desc">
            {filterTab === 'starred'
              ? 'Star any note to quickly find your most important notes here.'
              : selectedCategoryId
              ? 'Try selecting a different category or creating a new note.'
              : 'Notes you create will appear here. Capture your thoughts and organize them into categories.'}
          </p>
          {selectedCategoryId ? (
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setSelectedCategoryId(null)}
            >
              View All Notes
            </button>
          ) : filterTab === 'starred' ? (
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setFilterTab('all')}
            >
              Back to All Notes
            </button>
          ) : (
            <button
              type="button"
              className="btn-primary-custom"
              onClick={() => setShowCreate(true)}
            >
              <Icon name="plus" size={16} />
              Take First Note
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Notes display */}
          <div className={viewMode === 'grid' ? 'notes-responsive-grid docs-responsive-grid' : 'notes-list-stack docs-list-stack'}>
            {notes.map(note => (
              <NoteCard
                key={note.id}
                note={note}
                layout={viewMode}
                onClick={() => setViewNote(note)}
                onEdit={n => setEditNote(n)}
                onToggleStar={handleToggleStar}
                onDelete={n => {
                  if (localStorage.getItem('confirm_delete') === 'false') {
                    notesApi.delete(n.id).then(() => {
                      toast.success(`"${n.title}" moved to Trash (7-day retention)`);
                      loadNotes(page, sortBy, selectedCategoryId);
                    }).catch(err => toast.error(err.response?.data?.message || 'Failed to delete note'));
                  } else {
                    setDeleteNote(n);
                  }
                }}
              />
            ))}
          </div>

          {/* Pagination immediately after notes list */}
          <Pagination
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            size={PAGE_SIZE}
            onPageChange={handlePageChange}
            loading={loading}
          />
        </>
      )}

      {/* Modals */}
      {showCreate && (
        <NoteModal
          initialCategoryId={selectedCategoryId}
          initialFolderId={selectedCategoryId}
          onClose={() => setShowCreate(false)}
          onSaved={() => loadNotes(0, sortBy, selectedCategoryId)}
        />
      )}

      {editNote && (
        <NoteModal
          note={editNote}
          onClose={() => setEditNote(null)}
          onSaved={updated => {
            setNotes(prev => prev.map(n => n.id === updated.id ? updated : n));
            loadNotes(page, sortBy, selectedCategoryId);
          }}
        />
      )}

      {viewNote && (
        <NoteDetailModal
          note={viewNote}
          onClose={() => setViewNote(null)}
          onEdit={d => { setViewNote(null); setEditNote(d); }}
          onDelete={d => { setViewNote(null); setDeleteNote(d); }}
          onNoteUpdated={updated => {
            setNotes(prev => prev.map(n => n.id === updated.id ? updated : n));
          }}
        />
      )}

      {deleteNote && (
        <ConfirmDialog
          title="Move Note to Trash?"
          message={`"${deleteNote.title}" will be moved to Trash. You can recover it anytime within 7 days.`}
          confirmLabel="Move to Trash"
          onConfirm={handleDeleteNote}
          onCancel={() => setDeleteNote(null)}
          danger
        />
      )}
    </div>
  );
}
